import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import EnhancedSEOHead from '../components/EnhancedSEOHead';
import { validateSingleRoute } from '../utils/routeValidator';
import { getAllRoutes } from '../constants/routes';

const Enhanced404 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [suggestedRoutes, setSuggestedRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Log 404 for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', '404_error', {
        page_location: window.location.href,
        page_path: location.pathname,
        custom_parameter_1: 'Page Not Found'
      });
    }

    // Find suggested routes based on current path
    findSuggestedRoutes();
  }, [location.pathname]);

  const findSuggestedRoutes = async () => {
    setIsLoading(true);
    
    try {
      const allRoutes = getAllRoutes();
      const currentPath = location.pathname.toLowerCase();
      
      // Calculate similarity scores for all routes
      const routeScores = allRoutes
        .filter(route => route.path !== '*' && !route.redirect)
        .map(route => ({
          ...route,
          similarity: calculateSimilarity(currentPath, route.path.toLowerCase())
        }))
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 6); // Top 6 suggestions

      // Validate suggested routes are actually working
      const validatedRoutes = [];
      for (const route of routeScores.slice(0, 4)) {
        try {
          const validation = await validateSingleRoute(`${window.location.origin}${route.path}`);
          if (validation.isValid) {
            validatedRoutes.push(route);
          }
        } catch (error) {
          console.warn(`Failed to validate route ${route.path}:`, error);
          // Include route anyway if validation fails
          validatedRoutes.push(route);
        }
      }

      setSuggestedRoutes(validatedRoutes);
    } catch (error) {
      console.error('Error finding suggested routes:', error);
      // Fallback to basic popular routes
      setSuggestedRoutes([
        { path: '/', name: 'Homepage', title: 'Strategic Command Center' },
        { path: '/gcc-setup-services-comprehensive-mastery', name: 'GCC Setup Services', title: 'Comprehensive GCC Setup' },
        { path: '/executive-contact-strategic-engagement', name: 'Contact', title: 'Executive Contact' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSimilarity = (str1, str2) => {
    // Simple string similarity using Levenshtein distance
    const matrix = Array.from({ length: str1.length + 1 }, (_, i) => 
      Array.from({ length: str2.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );

    for (let i = 1; i <= str1.length; i++) {
      for (let j = 1; j <= str2.length; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j - 1] + 1
          );
        }
      }
    }

    const maxLength = Math.max(str1.length, str2.length);
    return maxLength === 0 ? 1 : 1 - matrix[str1.length][str2.length] / maxLength;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Redirect to search or filter routes
      const allRoutes = getAllRoutes();
      const matchingRoute = allRoutes.find(route => 
        route.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      if (matchingRoute) {
        navigate(matchingRoute.path);
      } else {
        // Redirect to homepage with search query
        navigate(`/?search=${encodeURIComponent(searchQuery)}`);
      }
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    if (window.history.length > 2) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  const reportBrokenLink = () => {
    const subject = encodeURIComponent(`Broken Link Report: ${location.pathname}`);
    const body = encodeURIComponent(`I found a broken link on your website:

URL: ${window.location.href}
Referrer: ${document.referrer || 'Direct access'}
Browser: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}

Please investigate and fix this broken link.

Thank you!`);
    
    window.location.href = `mailto:support@bootminds.com?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <EnhancedSEOHead
        title="404 - Page Not Found | Bootminds Elite"
        description="The page you're looking for doesn't exist. Explore our suggested pages or return to our homepage to find what you need."
        canonicalUrl="/404"
        organizationSchema={true}
      />
      
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <div className="text-center max-w-2xl mx-auto">
          {/* Error illustration */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="text-9xl font-bold text-text-primary select-none">404</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon 
                  name="Search" 
                  size={64} 
                  color="var(--color-primary)" 
                  className="opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Error message */}
          <h1 className="text-3xl font-bold text-text-primary mb-4">
            Oops! Page Not Found
          </h1>
          <p className="text-lg text-text-secondary mb-2">
            The page you're looking for doesn't exist or has been moved.
          </p>
          <p className="text-sm text-text-secondary mb-8">
            <strong>Requested URL:</strong> <code className="bg-muted px-2 py-1 rounded text-xs">{location.pathname}</code>
          </p>

          {/* Search form */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex max-w-md mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for pages..."
                className="flex-1 px-4 py-2 border border-border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                aria-label="Search for pages"
              />
              <Button
                type="submit"
                variant="primary"
                className="rounded-l-none border-l-0"
                iconName="Search"
                iconSize={18}
              >
                Search
              </Button>
            </div>
          </form>

          {/* Suggested routes */}
          {!isLoading && suggestedRoutes.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-text-primary mb-4">
                You might be looking for:
              </h2>
              <div className="grid gap-3 max-w-lg mx-auto">
                {suggestedRoutes.map((route, index) => (
                  <button
                    key={route.path}
                    onClick={() => navigate(route.path)}
                    className="flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors duration-200 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Navigate to ${route.name}`}
                  >
                    <div>
                      <div className="font-medium text-text-primary text-sm">
                        {route.name || route.title}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {route.path}
                      </div>
                    </div>
                    <Icon 
                      name="ArrowRight" 
                      size={16} 
                      color="var(--color-text-secondary)"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="mb-8">
              <div className="flex justify-center items-center space-x-2 text-text-secondary">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                <span className="text-sm">Finding suggested pages...</span>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              variant="primary"
              iconName="Home"
              iconPosition="left"
              onClick={handleGoHome}
              className="min-w-[140px]"
            >
              Go to Homepage
            </Button>

            <Button
              variant="outline"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleGoBack}
              className="min-w-[140px]"
            >
              Go Back
            </Button>

            <Button
              variant="ghost"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => window.location.reload()}
              className="min-w-[140px]"
            >
              Refresh Page
            </Button>
          </div>

          {/* Help section */}
          <div className="border-t border-border pt-8">
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Need Help?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
              <button
                onClick={reportBrokenLink}
                className="inline-flex items-center space-x-2 text-primary hover:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Icon name="Mail" size={16} />
                <span>Report Broken Link</span>
              </button>
              
              <a
                href="/contact"
                className="inline-flex items-center space-x-2 text-primary hover:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Icon name="MessageCircle" size={16} />
                <span>Contact Support</span>
              </a>
            </div>
          </div>

          {/* Additional context for crawlers */}
          <div className="sr-only">
            <h2>Popular Pages</h2>
            <ul>
              <li><a href="/">Homepage - Strategic Command Center</a></li>
              <li><a href="/services">GCC Setup Services</a></li>
              <li><a href="/market-intelligence">Market Intelligence Hub</a></li>
              <li><a href="/case-studies">Success Stories</a></li>
              <li><a href="/about">About Us</a></li>
              <li><a href="/contact">Contact Us</a></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default Enhanced404;